import { observer } from 'mobx-react';
import { type ComponentType, type ReactElement } from 'react';

// Hooks
import { useItemSheet } from 'hooks/use-item-sheet';

// Components
import ModifierDetails from 'components/cart-item-editor/ModifierDetails';

// Utils
import { formatCentsToPrice } from 'utils/Currency';

// Types
import ModifierGroup from 'models/ModifierGroup';
import Modifier from 'models/Modifier';

// Icons
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Container, Content, EditQuantity, Row, RowLeft, RowRight, Qty, UpdateButton } from './styles';

export interface CartItemEditorModifierControlProps {
  checked: boolean;
  children: ReactElement[];
  className: string;
  disabled: boolean;
  last: boolean;
  onChange: (ev: any) => void;
  value: any;
}

interface ModifierViewProps {
  group: ModifierGroup;
  menuItem: Modifier;
  currencyCode: string;
  last: boolean;
  Control: ComponentType<CartItemEditorModifierControlProps>;
  onChange?: (ev: any) => void;
}

function ModifierView({ group, menuItem, currencyCode, last, Control, onChange = () => {} }: ModifierViewProps) {
  const { displayedItem } = useItemSheet();

  const isSelected = displayedItem.selectedMods.includes(menuItem.id);
  const cartModifier = isSelected ? displayedItem.getCartModifier(group.id, menuItem.id) : undefined;

  const displayPrice = displayedItem?.getPrice(group.id, menuItem);

  const selectedModQuantity = (displayedItem.mods[group.id] ?? [])
    .filter((mod) => mod.selected)
    .reduce((total, mod) => total + mod.qty, 0);

  const showModifierDetails = Boolean(cartModifier && isSelected && menuItem?.hasMods());
  const maxQtyReached = selectedModQuantity === group?.max_selected;

  return (
    <Container last={last}>
      <Control
        checked={isSelected}
        className={cartModifier?.menuItem?.modifier_allow_quantities ? 'modifier-allow-quantities' : ''}
        disabled={!isSelected && maxQtyReached && !group.is_radio}
        onChange={onChange}
        value={menuItem.id}
      >
        <>
          {cartModifier?.menuItem?.modifier_allow_quantities && (
            <EditQuantity>
              <UpdateButton
                aria-label="Decrease quantity"
                data-testid="decrement-qty"
                disabled={cartModifier.qty < 1}
                onClick={(e) => {
                  e.preventDefault();
                  cartModifier.decrementModifierQty(group, menuItem.id, 1);
                }}
              >
                <div>
                  <MinusOutlined />
                </div>
              </UpdateButton>
              <Qty data-testid="modifier-qty-amount">{cartModifier.qty}</Qty>
              <UpdateButton
                aria-label="Increase quantity"
                data-testid="increment-qty"
                disabled={
                  cartModifier.qty > 98 ||
                  (cartModifier.menuItem.inventory_qty !== null &&
                    cartModifier.qty >= cartModifier.menuItem.inventory_qty) ||
                  maxQtyReached
                }
                onClick={(e) => {
                  e.preventDefault();
                  cartModifier.incrementModifierQty(group, menuItem.id, 1);
                }}
              >
                <div>
                  <PlusOutlined />
                </div>
              </UpdateButton>
            </EditQuantity>
          )}
        </>
        <Content>
          <Row>
            <RowLeft>{menuItem.name_for_customer}</RowLeft>
            <RowRight>
              <span data-testid="modifier-price">
                {displayPrice == null ? null : formatCentsToPrice(displayPrice, currencyCode)}
              </span>
            </RowRight>
          </Row>
        </Content>
      </Control>
      {cartModifier && showModifierDetails && <ModifierDetails cartModifier={cartModifier} />}
    </Container>
  );
}

export default observer(ModifierView);
